import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { api } from "core/utility";
import { snackbarStore } from "library/core/stores/snackbar/SnackbarStore";
import { SnackbarVariants } from "library/core/stores/snackbar/enums";
import TailwindBox from "library/components/_tailwind/box";
import TailwindFlex from "library/components/_tailwind/flex";
import TailwindButton from "library/components/_tailwind/button";
import TailwindTranslatedText from "library/components/_tailwind/translated-text";
import TailwindInput from "library/components/_tailwind/input";
import TailwindText from "library/components/_tailwind/text";
import TailwindSpinner from "library/components/_tailwind/spinner";
import { TailwindInputType } from "library/components/_tailwind/input-base";
import { IMember } from "../_stores/chat/interfaces";

const MAX_NOTE_LENGTH = 500;

const noteCache = new Map<string, string>();

type Props = {
  member: IMember;
  onClose: () => void;
};

const StreamNotePopup: React.FC<Props> = ({ member, onClose }) => {
  const intl = useIntl();
  const [note, setNote] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasExistingNote, setHasExistingNote] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [placement, setPlacement] = useState<"above" | "below" | null>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const updatePlacement = () => {
      if (!popupRef.current?.parentElement) return;
      const anchorRect = popupRef.current.parentElement.getBoundingClientRect();
      const popupHeight = popupRef.current.scrollHeight;
      const viewportHeight = document.documentElement.clientHeight;
      const spaceBelow = viewportHeight - anchorRect.bottom;
      const next = spaceBelow < popupHeight + 4 ? "above" : "below";
      setPlacement(prev => (prev === next ? prev : next));
    };

    updatePlacement();

    let rafId: number;
    const loop = () => {
      updatePlacement();
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafId);
  }, []);

  useEffect(() => {
    if (!member.pwsid) return;

    const cached = noteCache.get(member.pwsid);
    if (cached !== undefined) {
      setNote(cached);
      setHasExistingNote(cached.length > 0);
      return;
    }

    setIsLoading(true);
    api.broadcastStreamNotes
      .get({}, `?uid=${encodeURIComponent(member.pwsid)}`)
      .then(({ data }) => {
        const existingNote = data?.note || "";
        setNote(existingNote);
        setHasExistingNote(existingNote.length > 0);
        noteCache.set(member.pwsid!, existingNote);
      })
      .catch(() => {
        noteCache.set(member.pwsid!, "");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [member.pwsid]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const isDeleteAction = hasExistingNote && !note.trim();

  const handleDelete = useCallback(async () => {
    if (!member.pwsid) return;

    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }

    setIsSaving(true);
    try {
      await api.broadcastStreamNotes.delete(
        {},
        `?uid=${encodeURIComponent(member.pwsid)}`
      );

      noteCache.set(member.pwsid, "");

      snackbarStore.enqueueSnackbar({
        message: {
          id: "stream-note-popup.deleted",
          default: "Note deleted successfully",
        },
        variant: SnackbarVariants.SUCCESS,
      });
      onClose();
    } catch (error) {
      snackbarStore.enqueueSnackbar({
        message: {
          id: "stream-note-popup.delete-error",
          default: "Failed to delete note",
        },
        variant: SnackbarVariants.ERROR,
      });
    } finally {
      setIsSaving(false);
    }
  }, [member.pwsid, confirmingDelete, onClose]);

  const handleSave = useCallback(async () => {
    if (!note.trim() || !member.pwsid) return;

    setIsSaving(true);
    try {
      if (hasExistingNote) {
        await api.broadcastStreamNotes.patch(
          { note: note.trim() },
          `?uid=${encodeURIComponent(member.pwsid)}`
        );
      } else {
        await api.broadcastStreamNotes.post({
          note: note.trim(),
          uid: member.pwsid,
        });
      }

      noteCache.set(member.pwsid, note.trim());

      snackbarStore.enqueueSnackbar({
        message: {
          id: "stream-note-popup.success",
          default: "Note saved successfully",
        },
        variant: SnackbarVariants.SUCCESS,
      });
      onClose();
    } catch (error) {
      snackbarStore.enqueueSnackbar({
        message: {
          id: "stream-note-popup.error",
          default: "Failed to save note",
        },
        variant: SnackbarVariants.ERROR,
      });
    } finally {
      setIsSaving(false);
    }
  }, [note, member.pwsid, hasExistingNote, onClose]);

  return (
    <TailwindBox
      ref={popupRef}
      position='absolute'
      style={{
        right: 0,
        zIndex: 999999,
        visibility: placement === null ? "hidden" : "visible",
        ...(placement === "above"
          ? { bottom: "100%", marginBottom: 4 }
          : { top: "100%", marginTop: 4 }),
      }}
      width={"w-80"}
      backgroundColor={"bg-primary-bg-color"}
      borderRadius={"rounded-lg"}
      borderWidth={["border"]}
      borderColor={"border-main-color"}
      padding={["p-3"]}
      className={["shadow-lg"]}>
      <TailwindFlex flexDirection={"flex-col"} gap={"gap-2"}>
        <TailwindText
          fontWeight={"font-bold"}
          fontSize={"text-sm"}
          textColor={"text-main-color"}>
          {intl.formatMessage(
            {
              id: "stream-note-popup.title",
              defaultMessage: "Note for {username}",
            },
            { username: member.username || "" }
          )}
        </TailwindText>

        {isLoading ? (
          <TailwindFlex
            justifyContent={"justify-center"}
            alignItems={"items-center"}
            height={"h-24"}>
            <TailwindSpinner />
          </TailwindFlex>
        ) : (
          <TailwindInput
            type={TailwindInputType.textarea}
            placeholder={intl.formatMessage({
              id: "stream-note-popup.placeholder",
              defaultMessage: "Type your note here...",
            })}
            onChange={e => {
              setNote(e.target.value);
              if (e.target.value.trim()) setConfirmingDelete(false);
            }}
            value={note}
            maxLength={MAX_NOTE_LENGTH}
            backgroundColor={"secondary"}
            maxRows={4}
            boxProps={{
              borderColor: "border-main-color",
              borderWidth: ["border"],
              borderRadius: "rounded-lg",
              padding: ["px-3", "py-2"],
              height: "h-auto",
              overflow: "overflow-y-auto",
            }}
          />
        )}

        <TailwindFlex
          justifyContent={"justify-between"}
          alignItems={"items-center"}>
          <TailwindText fontSize={"text-xs"} textColor={"text-main-color"}>
            {note.length}/{MAX_NOTE_LENGTH}
          </TailwindText>
          <TailwindFlex gap={"gap-2"} width={"w-auto"}>
            <TailwindButton
              onClick={onClose}
              backgroundColor={"bg-gray-500"}
              padding={["px-3", "py-1"]}
              rounded={true}
              size={"xs"}>
              <TailwindTranslatedText
                descriptor={{
                  id: "stream-note-popup.cancel",
                  defaultMessage: "Cancel",
                }}
              />
            </TailwindButton>

            <TailwindButton
              onClick={isDeleteAction ? handleDelete : handleSave}
              backgroundColor={isDeleteAction ? "bg-red-500" : "bg-green-500"}
              padding={["px-3", "py-1"]}
              rounded={true}
              size={"xs"}
              disabled={(!isDeleteAction && !note.trim()) || isSaving || isLoading}>
              <TailwindTranslatedText
                descriptor={
                  isSaving
                    ? {
                        id: "stream-note-popup.saving",
                        defaultMessage: "Saving...",
                      }
                    : isDeleteAction
                    ? confirmingDelete
                      ? {
                          id: "stream-note-popup.confirm",
                          defaultMessage: "Confirm?",
                        }
                      : {
                          id: "stream-note-popup.delete",
                          defaultMessage: "Delete",
                        }
                    : {
                        id: "stream-note-popup.save",
                        defaultMessage: "Save",
                      }
                }
              />
            </TailwindButton>
          </TailwindFlex>
        </TailwindFlex>
      </TailwindFlex>
    </TailwindBox>
  );
};

export default StreamNotePopup;
